import { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UploadCloud, ScanLine, Trash2, FileText, Wand2, ArrowRight, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { inr, ymd, today } from "@/lib/format";

export default function Bills() {
  const [bills, setBills] = useState([]);
  const [scanOpen, setScanOpen] = useState(false);

  const load = () => api.get("/bills").then(({ data }) => setBills(data));
  useEffect(() => { load(); }, []);

  const statusBadge = (s) => {
    if (s === "converted") return "bg-emerald-100 text-emerald-700 border-emerald-200";
    if (s === "cancelled") return "bg-slate-100 text-slate-500 border-slate-200";
    return "bg-indigo-100 text-indigo-700 border-indigo-200";
  };

  const del = async (id) => {
    if (!window.confirm("Delete this bill record?")) return;
    await api.delete(`/bills/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <PageHeader
        title="Supplier Bills"
        subtitle="Scan a supplier invoice — AI extracts the details, you review, save & optionally convert to a PO"
        action={
          <Button
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            onClick={() => setScanOpen(true)}
            data-testid="scan-bill-btn"
          >
            <ScanLine className="w-4 h-4 mr-1" /> Scan Invoice
          </Button>
        }
      />

      <ScanDialog open={scanOpen} onOpenChange={setScanOpen} onSaved={load} />

      <div className="px-8 py-6">
        <Card className="border-slate-200 overflow-hidden">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Invoice #</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Supplier</TableHead>
                <TableHead>GSTIN</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Attachment</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bills.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-10 text-slate-500">
                    No bills yet. Click <b>Scan Invoice</b> to add one.
                  </TableCell>
                </TableRow>
              )}
              {bills.map((b) => (
                <TableRow key={b.bill_id}>
                  <TableCell className="font-medium">{b.invoice_number || "—"}</TableCell>
                  <TableCell className="text-slate-500">{ymd(b.invoice_date)}</TableCell>
                  <TableCell>{b.supplier_name || "—"}</TableCell>
                  <TableCell className="text-slate-500">{b.supplier_gstin || "—"}</TableCell>
                  <TableCell className="text-right number-cell font-semibold">{inr(b.total)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`uppercase text-[10px] tracking-wider ${statusBadge(b.status)}`}>
                      {b.status}
                    </Badge>
                    {b.converted_doc_id && (
                      <Link to={`/documents/${b.converted_doc_id}`} className="text-xs text-indigo-700 hover:underline ml-2">
                        view PO
                      </Link>
                    )}
                  </TableCell>
                  <TableCell>
                    {b.storage_path ? (
                      <FileLink path={b.storage_path} name={b.file_name} />
                    ) : "—"}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    {!b.converted_doc_id && b.supplier_id && (
                      <ConvertButton bill={b} onDone={load} />
                    )}
                    <Button variant="ghost" size="icon" onClick={() => del(b.bill_id)}>
                      <Trash2 className="w-4 h-4 text-rose-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}

function FileLink({ path, name }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    let blobUrl = null;
    api.get(`/files/${path}`, { responseType: "blob" }).then(({ data }) => {
      if (!alive) return;
      blobUrl = URL.createObjectURL(data);
      setUrl(blobUrl);
    }).catch(() => {});
    return () => { alive = false; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [path]);
  if (!url) return <span className="text-xs text-slate-400 inline-flex items-center gap-1"><Paperclip className="w-3 h-3" /> loading…</span>;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="text-indigo-700 hover:underline text-xs inline-flex items-center gap-1">
      <FileText className="w-3 h-3" /> {name || "attachment"}
    </a>
  );
}

function ConvertButton({ bill, onDone }) {
  const convert = async () => {
    if (!window.confirm(`Convert bill ${bill.invoice_number} into a Purchase Order?`)) return;
    try {
      const { data } = await api.post(`/bills/${bill.bill_id}/convert-to-po`);
      toast.success(`Created ${data.number}`);
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };
  return (
    <Button variant="outline" size="sm" onClick={convert} data-testid={`convert-bill-${bill.bill_id}`}>
      <ArrowRight className="w-3 h-3 mr-1" /> To PO
    </Button>
  );
}

function ScanDialog({ open, onOpenChange, onSaved }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploaded, setUploaded] = useState(null);   // {file_id, storage_path,...}
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const emptyForm = () => ({
    supplier_id: "",
    supplier_name: "",
    supplier_gstin: "",
    invoice_number: "",
    invoice_date: today(),
    due_date: "",
    interstate: false,
    items: [],
    subtotal: 0, cgst: 0, sgst: 0, igst: 0, total: 0,
    notes: "",
    file_id: null,
  });
  const [form, setForm] = useState(emptyForm());

  useEffect(() => {
    if (open) {
      api.get("/parties", { params: { kind: "supplier" } }).then(({ data }) => setSuppliers(data));
    } else {
      // reset
      setFile(null); setUploaded(null); setForm(emptyForm());
      if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    }
    // eslint-disable-next-line
  }, [open]);

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) return toast.error("File exceeds 10 MB");
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));

    // Upload immediately
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/bills/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploaded(data);
      setForm((x) => ({ ...x, file_id: data.file_id }));
      toast.success("File uploaded. Now click 'Auto-extract'.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const runExtract = async () => {
    if (!uploaded) return toast.error("Upload a file first");
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file_id", uploaded.file_id);
      const { data } = await api.post("/bills/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const ex = data.extracted || {};
      setForm((f) => ({
        ...f,
        supplier_name: ex.supplier_name || "",
        supplier_gstin: ex.supplier_gstin || "",
        invoice_number: ex.invoice_number || "",
        invoice_date: (ex.invoice_date || today()).slice(0, 10),
        due_date: (ex.due_date || "").slice(0, 10),
        interstate: !!ex.interstate,
        items: Array.isArray(ex.items) ? ex.items.map((it) => ({
          description: it.description || "",
          hsn: it.hsn || "",
          unit: it.unit || "PCS",
          quantity: Number(it.quantity) || 0,
          rate: Number(it.rate) || 0,
          gst_rate: Number(it.gst_rate) || 0,
          amount: Number(it.amount) || (Number(it.quantity)||0) * (Number(it.rate)||0),
        })) : [],
        subtotal: Number(ex.subtotal) || 0,
        cgst: Number(ex.cgst) || 0,
        sgst: Number(ex.sgst) || 0,
        igst: Number(ex.igst) || 0,
        total: Number(ex.total) || 0,
        notes: ex.notes || "",
      }));
      // Try match supplier by GSTIN or name
      const match = suppliers.find(
        (s) => (ex.supplier_gstin && s.gstin === ex.supplier_gstin) ||
               (ex.supplier_name && s.name.toLowerCase() === (ex.supplier_name || "").toLowerCase())
      );
      if (match) setForm((f) => ({ ...f, supplier_id: match.party_id, supplier_name: match.name }));
      toast.success("Extraction complete — please review & save");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Extraction failed");
    } finally {
      setExtracting(false);
    }
  };

  const updateLine = (i, patch) => setForm((f) => {
    const items = [...f.items]; items[i] = { ...items[i], ...patch }; return { ...f, items };
  });
  const removeLine = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const addLine = () => setForm((f) => ({ ...f, items: [...f.items, { description: "", hsn: "", unit: "PCS", quantity: 1, rate: 0, gst_rate: 18, amount: 0 }] }));

  // Recompute totals when items change
  const totals = useMemo(() => {
    let sub = 0, cgst = 0, sgst = 0, igst = 0;
    for (const l of form.items) {
      const amt = Number(l.quantity || 0) * Number(l.rate || 0);
      sub += amt;
      const g = amt * Number(l.gst_rate || 0) / 100;
      if (form.interstate) igst += g; else { cgst += g / 2; sgst += g / 2; }
    }
    return { sub, cgst, sgst, igst, total: sub + cgst + sgst + igst };
  }, [form.items, form.interstate]);

  const save = async () => {
    if (!form.invoice_number) return toast.error("Invoice number is required");
    if (!form.total && !totals.total) return toast.error("Total is required");
    setSaving(true);
    try {
      const payload = {
        ...form,
        subtotal: totals.sub || form.subtotal,
        cgst: totals.cgst || form.cgst,
        sgst: totals.sgst || form.sgst,
        igst: totals.igst || form.igst,
        total: totals.total || form.total,
        supplier_id: form.supplier_id || null,
      };
      await api.post("/bills", payload);
      toast.success("Bill saved");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-indigo-600" /> Scan Supplier Invoice
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* LEFT — file upload + preview */}
          <div className="space-y-3">
            <div
              onClick={() => inputRef.current?.click()}
              className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/50 transition-colors"
              data-testid="drop-zone"
            >
              <UploadCloud className="w-8 h-8 mx-auto text-slate-400" />
              <div className="mt-2 text-sm font-medium">
                {file ? file.name : "Click to upload"}
              </div>
              <div className="text-xs text-slate-500 mt-1">JPG, PNG or PDF · up to 10 MB</div>
              <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,application/pdf"
                onChange={onPickFile}
                className="hidden"
                data-testid="file-input"
              />
            </div>

            {previewUrl && (
              <div className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
                {file?.type === "application/pdf" ? (
                  <embed src={previewUrl} type="application/pdf" className="w-full h-96" />
                ) : (
                  <img src={previewUrl} alt="preview" className="w-full max-h-96 object-contain" />
                )}
              </div>
            )}

            <Button
              onClick={runExtract}
              disabled={!uploaded || extracting}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="extract-btn"
            >
              <Wand2 className="w-4 h-4 mr-2" />
              {extracting ? "Reading invoice with Gemini 3 Flash…" : "Auto-extract with AI"}
            </Button>
          </div>

          {/* RIGHT — review form */}
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <F label="Match to Supplier">
                <Select value={form.supplier_id || "_none"} onValueChange={(v) => {
                  const sup = suppliers.find(s => s.party_id === v);
                  setForm((f) => ({
                    ...f,
                    supplier_id: v === "_none" ? "" : v,
                    supplier_name: sup ? sup.name : f.supplier_name,
                    supplier_gstin: sup ? sup.gstin : f.supplier_gstin,
                  }));
                }}>
                  <SelectTrigger data-testid="bill-supplier-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">— Not linked —</SelectItem>
                    {suppliers.map((s) => (<SelectItem key={s.party_id} value={s.party_id}>{s.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </F>
              <F label="Supplier Name">
                <Input value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} />
              </F>
              <F label="Supplier GSTIN">
                <Input value={form.supplier_gstin} onChange={(e) => setForm({ ...form, supplier_gstin: e.target.value })} />
              </F>
              <F label="Invoice #">
                <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} data-testid="bill-invoice-number" />
              </F>
              <F label="Invoice Date">
                <Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} />
              </F>
              <F label="Due Date">
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </F>
              <F label="Tax Type">
                <div className="flex items-center gap-3 pt-2">
                  <Switch checked={form.interstate} onCheckedChange={(v) => setForm({ ...form, interstate: v })} />
                  <span className="text-sm text-slate-600">{form.interstate ? "IGST (inter-state)" : "CGST + SGST"}</span>
                </div>
              </F>
              <F label="Notes">
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </F>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-600">Line Items</div>
                <Button size="sm" variant="outline" onClick={addLine}>+ Add row</Button>
              </div>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left p-2">Description</th>
                      <th className="p-2">HSN</th>
                      <th className="text-right p-2">Qty</th>
                      <th className="text-right p-2">Rate</th>
                      <th className="text-right p-2">GST%</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.length === 0 && (
                      <tr><td colSpan={6} className="p-3 text-center text-slate-500">No line items yet.</td></tr>
                    )}
                    {form.items.map((l, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="p-1"><Input className="h-8" value={l.description} onChange={(e) => updateLine(i, { description: e.target.value })} /></td>
                        <td className="p-1"><Input className="h-8 w-20" value={l.hsn} onChange={(e) => updateLine(i, { hsn: e.target.value })} /></td>
                        <td className="p-1"><Input type="number" className="h-8 w-16 text-right" value={l.quantity} onChange={(e) => updateLine(i, { quantity: +e.target.value })} /></td>
                        <td className="p-1"><Input type="number" className="h-8 w-20 text-right" value={l.rate} onChange={(e) => updateLine(i, { rate: +e.target.value })} /></td>
                        <td className="p-1"><Input type="number" className="h-8 w-16 text-right" value={l.gst_rate} onChange={(e) => updateLine(i, { gst_rate: +e.target.value })} /></td>
                        <td className="p-1"><Button variant="ghost" size="icon" onClick={() => removeLine(i)}><Trash2 className="w-4 h-4 text-rose-500" /></Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="border-t border-slate-200 pt-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="number-cell">{inr(totals.sub)}</span></div>
              {form.interstate
                ? <div className="flex justify-between"><span className="text-slate-500">IGST</span><span className="number-cell">{inr(totals.igst)}</span></div>
                : <>
                    <div className="flex justify-between"><span className="text-slate-500">CGST</span><span className="number-cell">{inr(totals.cgst)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">SGST</span><span className="number-cell">{inr(totals.sgst)}</span></div>
                  </>}
              <div className="flex justify-between font-display font-bold text-base pt-1 border-t border-slate-200">
                <span>Total</span><span className="number-cell">{inr(totals.total)}</span>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={save} disabled={saving} data-testid="save-bill-btn">
            {saving ? "Saving…" : "Save Bill"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children }) {
  return <div className="space-y-1"><Label className="text-xs text-slate-600">{label}</Label>{children}</div>;
}
